import { useState, useEffect, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios, { CancelTokenSource } from 'axios'
import { Button, BUTTON_TYPES } from 'components/Button'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { Table } from 'components/Table'
import { Input } from 'components/Input'
import { UserArchivePopup } from 'pages/CRUDUser/UserArchivePopup'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { useDebounce } from 'hooks/hooks'
import useRouter from 'hooks/useRouter'
import { getCommonsUsers } from './api'
import { updateLoadingStack } from 'redux/store/common/slice'
import { routes } from 'router/Config/config.routes'
import { getUser } from 'redux/store/user/getters'
import { getAllowedRolesList } from '../../../../redux/store/myCommunity/getters'
import { fullFormatPhoneNumber, isTableHasParams } from 'helper/common'
import { ability, ACTIONS, SUBJECTS, Can, ROLES } from 'features/Permission'
import { BTN_TXT, PLACEHOLDERS } from 'constants/txt'
import { USER_STATUSES } from 'constants/common'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { columns, AVAILABLE_FOR_CCS } from './constants'
import { onStartChat } from '../../utils'
import { MY_COMMUNITY_KEYS } from '../../constants'
import { ColumnsType } from 'antd/es/table'
import {
  CommonsUser,
  CommonsUsersFiltersFunc,
  CommonsUsersRequestData,
  CommonsUsersParams,
  CommonsUsersFilters
} from './types'
import { UserRolesFilterComponent } from 'components/UserRolesFilterComponent/UserRolesFilterComponent'
import './styles.scss'
import { FETCH_STATUS } from '../../../../redux/store/common/types'

export const CommonsUsers = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [tableData, setTableData] = useState<CommonsUser[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<CommonsUser[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] = useState<CommonsUsersRequestData>()
  const [filters, setFilters] = useState<CommonsUsersFilters>({})
  const [tableColumns, setTableColumns] = useState<ColumnsType<CommonsUser>>([])
  const [totalNumber, setTotalNumber] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const user = useSelector(getUser)
  const allowedRoles = useSelector(getAllowedRolesList)
  const dispatch = useDispatch()
  const { push } = useRouter()
  const debouncedSearch = useDebounce(searchValue, 500)

  const rolesOptions = useMemo(
    () => allowedRoles.find((i) => i.tab_name === 'commons')?.roles || [],
    [allowedRoles]
  )

  const isCCS = useMemo(
    () => Boolean(user.role === ROLES.COMMONS_CONTRACT_STEWARD),
    [user.role]
  )

  const isNotAvailableForCCS = useMemo(() => {
    if (!activeTab) return false
    return isCCS && !AVAILABLE_FOR_CCS.find((i) => i === activeTab)
  }, [activeTab, isCCS])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: CommonsUsersRequestData) => {
    dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: CommonsUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getCommonsUsers(activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        setTableColumns(
          columns({
            applyFilters,
            filters,
            isNotAvailableForCCS,
            onStartChat,
            role: activeTab ?? ''
          })
        )
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() =>
        dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
      )
  }

  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize: number) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const createUser = () =>
    push(`${routes.createUserProfile}/new?fromCommunity=true`, {
      state: { redirectTab: MY_COMMUNITY_KEYS.commons }
    })

  const handleRedirect = () =>
    push(
      `${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`,
      {
        state: { redirectTab: MY_COMMUNITY_KEYS.commons }
      }
    )

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const onChangeRole = (tab?: string) => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFilters({})
    setSortParams({})
    setSearchValue('')
    setActiveTab(tab ?? null)
    setTableColumns([])
  }

  const applyFilters: CommonsUsersFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  return (
    <div className="commons-users-list mt-16">
      <div className="commons-users-list__header row align-center space-between mt-16">
        <div className="row gap-8">
          <UserRolesFilterComponent
            roles={rolesOptions}
            onChange={onChangeRole}
            groupRoles={false}
          />
          <div className="commons-users-list__actions row align-center">
            {!!selectedRowKeys.length ? (
              <Typography.Body1
                className="table-wrapper__select-title"
                weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
              >
                Selected {selectedRowKeys.length} of {totalNumber}
              </Typography.Body1>
            ) : (
              <Input
                className="commons-users-list__search allow-clear"
                propsInput={{
                  allowClear: { clearIcon: <Close16 /> },
                  placeholder: PLACEHOLDERS.SEARCH_RFP_USERS,
                  prefix: <Search16 />,
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value)
                }}
              />
            )}
          </div>
        </div>
        <Can I={ACTIONS.CRUD} a={SUBJECTS.COMMONS_USERS}>
          <div className="row align-center gap-8">
            {!selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                onClick={handleRedirect}
                icon={<Edit16 />}
                upperCase
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {!!selectedRows.length &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length && (
                <UserArchivePopup<CommonsUser>
                  selected={selectedRows}
                  callback={() => {
                    setSelectedRowKeys([])
                    setSelectedRows([])
                    getTableData({ ...sortParams, ...filters })
                  }}
                  isTable
                />
              )}
          </div>
        </Can>
      </div>
      {!firstLoad && (
        <Table<CommonsUser>
          dataSource={!tableColumns.length ? [] : tableData}
          columns={tableColumns}
          className="community-users-table mt-8"
          emptyText="No users found"
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          onChangePage={handleChangePageSize}
          onChange={handleTableChange}
          hasSearchOrFilters={isTableHasParams<CommonsUsersFilters>(
            debouncedSearch,
            filters
          )}
          createButton={
            ability.can(ACTIONS.CRUD, SUBJECTS.COMMONS_USERS)
              ? {
                  buttonText: BTN_TXT.CREATE_USER,
                  availability: true,
                  action: createUser
                }
              : undefined
          }
          rowSelection={
            ability.can(ACTIONS.CRUD, SUBJECTS.COMMONS_USERS)
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled: record.uuid === user.uuid || isNotAvailableForCCS
                  })
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
